import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import ThunderstormIcon from '@mui/icons-material/Thunderstorm';
import { keyframes } from "@mui/system";
import Fade from "@mui/material/Fade";
import Typography from "@mui/material/Typography";

// Animación sutil para SEV
const pulse = keyframes`
  0%   { transform: scale(1);    opacity: 1;    box-shadow: 0 0 0 rgba(255,255,255,0); }
  50%  { transform: scale(1.04); opacity: 1;    box-shadow: 0 0 15px rgba(229,50,45,0.4); }
  100% { transform: scale(1);    opacity: 1;    box-shadow: 0 0 0 rgba(255,255,255,0); }
`;

const LEVELS = [
  { label: "Light", code: "LGT", start: "#ffff00", end: "#ffff00" },
  { label: "Moderate", code: "MOD", start: "#ff8c00", end: "#ff8c00" },
  { label: "Severe", code: "SEV", start: "#e5322d", end: "#e5322d" },
];

const containerSx = (hideBelowWidth = 0) => ({
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
  mt: -1,
  opacity: 1,
  transition: "opacity .25s ease",
  [`@media (max-width:${hideBelowWidth}px)`]: { opacity: 0 },
});

function overlayFor(levelCode, variant) {
  const code = `${levelCode}`.toUpperCase();
  if (variant === "pattern") {
    if (code.includes("LGT")) return { background: `radial-gradient(rgba(0,0,0,.12) 1px, transparent 1px)`, backgroundSize: "5px 5px", opacity: 0.4 };
    if (code.includes("MOD")) return { background: `repeating-linear-gradient(45deg, rgba(0,0,0,.1) 0 6px, transparent 6px 12px)`, opacity: 0.4 };
    return { background: `repeating-linear-gradient(45deg, rgba(255,255,255,.2) 0 6px, transparent 6px 12px)`, opacity: 0.5 };
  }
  return undefined;
}

function textColorFor(hex) {
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150 ? "#000000" : "#FFFFFF"; // Contraste máximo
}

export default function RiskSelectorCNN_VERTICAL({
  levels = LEVELS,
  hideBelowWidth = 0,
  interactive = false,
  onSelect,
  size = "mini",
  symbolVariant = "pattern",
  equalWidth = true,
  activeLayer = 'CNN:EU_025_live_demo',
}) {
  const isMini = size === "mini";

  const densityThresholds = {
    'CNN:EU_025_live_demo': {
      light: '1',
      moderate: '50',
      high: '500',
    },
    'CNN:EU_01_live_demo': {
      light: 1,
      moderate: 8.0,
      high: 80,
    }
  };

  const activeThresholds = densityThresholds[activeLayer] || densityThresholds['CNN:EU_025_live_demo'];
  const [showTooltips, setShowTooltips] = useState(true);
  const [hoveredLevel, setHoveredLevel] = useState(null);
  const [isTitleHovered, setIsTitleHovered] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowTooltips(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const buttonWidth = equalWidth ? (isMini ? 75 : 85) : 75;

  return (
    <Box sx={{
      position: "fixed",
      bottom: { xs: 335, md: 280 }, // ~14px sobre el botón POLYGONS (3º de la pila en map-layers.js); en xs se escala por el zoom
      left: { xs: 65, md: 55 },
      zIndex: 1000,
      zoom: { xs: 0.8, md: 1 }
    }}>
      {/* Rotated Wrapper for Legend Bar & Title */}
      <Box sx={{
        transform: "rotate(-90deg)",
        transformOrigin: "bottom left",
      }}>
        <Box
          component="span"
          onMouseEnter={() => setIsTitleHovered(true)}
          onMouseLeave={() => setIsTitleHovered(false)}
          sx={{
            display: 'block',
            width: '100%',
            textAlign: 'center',
            mb: 2.0, // Mayor separación como pediste
            typography: 'caption',
            fontWeight: 900,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
            color: '#FFFFFF', // Blanco sólido
            opacity: 1,      // Sin opacidad para que se vea intenso
            textShadow: '0 2px 4px rgba(0,0,0,0.5)', // Sombra para resaltar sobre el mapa
            lineHeight: 1,
            cursor: 'help',
            pointerEvents: 'auto'
          }}
        >
          <ThunderstormIcon sx={{ fontSize: 13, color: 'inherit', marginRight: 0.7, verticalAlign: 'middle' }} aria-hidden />
          Convective Risk
        </Box>

        <Box sx={containerSx(hideBelowWidth)}>
          <Box sx={{
            display: "inline-flex",
            borderRadius: 999,
            pointerEvents: "auto",
            boxShadow: "0 8px 25px rgba(0,0,0,0.4)" // Sombra exterior más fuerte
          }}>
            {levels.map((lvl, i) => {
              const code = (lvl.code ?? lvl.label).toString().toUpperCase();
              const after = overlayFor(code, symbolVariant);
              const isSevere = code.includes("SEV");
              const isFirst = i === 0;
              const isLast = i === levels.length - 1;

              return (
                <Tooltip key={lvl.code} title={`${lvl.label}`} arrow placement="top" disableInteractive>
                  <Button
                    disableElevation
                    disableRipple
                    onClick={interactive && onSelect ? () => onSelect(lvl.code) : undefined}
                    onMouseEnter={() => setHoveredLevel(lvl.code)}
                    onMouseLeave={() => setHoveredLevel(null)}
                    sx={{
                      position: "relative",
                      minWidth: buttonWidth,
                      px: isMini ? 1.2 : 1.5,
                      py: isMini ? 0.6 : 0.8,
                      fontSize: isMini ? ".65rem" : ".75rem",
                      fontWeight: 800,
                      lineHeight: 1,
                      textTransform: "none",
                      color: lvl.code === "SEV" ? "#000000" : textColorFor(lvl.start),
                      backgroundColor: lvl.start, // COLOR INTENSO SÓLIDO
                      borderRadius: 0,
                      borderTopLeftRadius: isFirst ? 999 : 0,
                      borderBottomLeftRadius: isFirst ? 999 : 0,
                      borderTopRightRadius: isLast ? 999 : 0,
                      borderBottomRightRadius: isLast ? 999 : 0,
                      borderRight: i < levels.length - 1 ? "1px solid rgba(0,0,0,0.1)" : 0,
                      cursor: "default",
                      transition: "transform .2s ease, filter .2s ease",

                      "&:hover": {
                        backgroundColor: lvl.start,
                        filter: "brightness(1.15)",
                        zIndex: 2,
                      },

                      '&::after': after && after.background ? {
                        content: '""', position: 'absolute', inset: 0,
                        background: after.background, opacity: after.opacity,
                        mixBlendMode: 'multiply', pointerEvents: 'none'
                      } : undefined,
                    }}
                  >
                    {lvl.label}
                  </Button>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Non-Rotated Wrapper for Tooltips (Crisp text, zero distortion, centered next to each segment) */}
      {levels.map((lvl, i) => {
        // Calculate the center offset for each segment from the bottom pivot of the bar
        const centerOffset = i * buttonWidth + (buttonWidth / 2);

        return (
          <Fade key={lvl.code} in={showTooltips || hoveredLevel === lvl.code} timeout={400}>
            <Box
              sx={{
                position: "absolute",
                bottom: `${centerOffset}px`,
                left: "18px", // Adjusted distance next to the color block
                transform: "translateY(50%)", // Perfect vertical centering next to the block
                backgroundColor: "rgba(15, 23, 42, 0.95)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                borderRadius: "12px",
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.45)",
                padding: "8px 12px",
                zIndex: 10000,
                whiteSpace: "nowrap",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "2px",
                pointerEvents: "none",

                // Speech bubble pointer pointing left towards the color block
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "50%",
                  left: "-5px",
                  transform: "translateY(-50%) rotate(45deg)",
                  width: "8px",
                  height: "8px",
                  backgroundColor: "rgba(15, 23, 42, 0.95)",
                  borderLeft: "1px solid rgba(255, 255, 255, 0.15)",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.15)",
                }
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 900,
                  color: lvl.start === "#ffff00" ? "#ffd700" : lvl.start, // Level color with high contrast
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  lineHeight: 1.2
                }}
              >
                {lvl.label}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 500,
                  color: "rgba(255, 255, 255, 0.72)",
                  lineHeight: 1.2
                }}
              >
                {lvl.code === "LGT" && `Lightning Density (>= ${activeThresholds.light} counts/km²)`}
                {lvl.code === "MOD" && `Lightning Density (> ${activeThresholds.moderate} counts/km²)`}
                {lvl.code === "SEV" && `Lightning Density (> ${activeThresholds.high} counts/km²)`}
              </Typography>
            </Box>
          </Fade>
        );
      })}

      {/* Title Tooltip */}
      <Fade in={showTooltips || isTitleHovered} timeout={400}>
        <Box
          sx={{
            position: "absolute",
            bottom: `${levels.length * buttonWidth + 40}px`,
            left: "18px", // Adjusted distance next to the color block
            transform: "translateY(50%)", // Perfect vertical centering next to the block
            backgroundColor: "rgba(15, 23, 42, 0.95)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.45)",
            padding: "8px 12px",
            zIndex: 10000,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "2px",
            pointerEvents: "none",

            // Speech bubble pointer pointing downwards
            "&::before": {
              content: '""',
              position: "absolute",
              bottom: "-5px",
              left: "20px", // Ajustado para que no quede muy al borde
              transform: "rotate(45deg)",
              width: "8px",
              height: "8px",
              backgroundColor: "rgba(15, 23, 42, 0.95)",
              borderRight: "1px solid rgba(255, 255, 255, 0.15)",
              borderBottom: "1px solid rgba(255, 255, 255, 0.15)",
            }
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.68rem",
              fontWeight: 900,
              color: "#FFFFFF",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              lineHeight: 1.2
            }}
          >
            CONVECTIVE RISK
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.62rem",
              fontWeight: 500,
              color: "rgba(255, 255, 255, 0.72)",
              lineHeight: 1.2,
              whiteSpace: "normal", // Allow wrapping for long text
              maxWidth: "200px" // Restrict width so it wraps nicely
            }}
          >
            Lightning count density is defined as the number of lightning detections normalised by grid-cell area, expressed in counts/km²
          </Typography>
        </Box>
      </Fade>
    </Box>
  );
}